/** Path-based HRM navigation (replaces `window.location.hash`). */

export const DEFAULT_HRM_PAGE = 'dashboard';

export function pathToPage(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/' || normalized === '') {
    return DEFAULT_HRM_PAGE;
  }
  const segments = normalized.startsWith('/')
    ? normalized.slice(1).split('/').filter(Boolean)
    : normalized.split('/').filter(Boolean);

  if (segments[0] === 'attendance' && segments[1] === 'regularizations') {
    return 'regularization';
  }

  return decodeURIComponent(segments[0] || DEFAULT_HRM_PAGE);
}

export function pageToPath(page: string): string {
  const key = (page || DEFAULT_HRM_PAGE).trim();
  return `/${encodeURIComponent(key)}`;
}

/** One-time migration: `/#punch-clock` → `/punch-clock` */
export function migrateHashRouteToPath(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.slice(1).trim();
  if (!hash) return null;
  const path = pageToPath(hash);
  window.history.replaceState(null, '', path);
  return pathToPage(path);
}

/** Path may be a page key (`onboarding`) or a path with query (`/onboarding?employeeId=…`). */
type NavigateFn = (pathOrPage: string) => void;

let navigateImpl: NavigateFn | null = null;

export function registerHrmNavigator(fn: NavigateFn | null): void {
  navigateImpl = fn;
}

export function buildHrmPath(page: string, query?: Record<string, string>): string {
  const base = pageToPath(page);
  if (!query || !Object.keys(query).length) return base;
  return `${base}?${new URLSearchParams(query).toString()}`;
}

/** Read deep-link params for onboarding (`?employeeId=` or `?caseId=`). */
export function readOnboardingDeepLink(search: string): {
  employeeId?: string;
  caseId?: string;
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const employeeId = params.get('employeeId')?.trim() || undefined;
  const caseId = params.get('caseId')?.trim() || undefined;
  return { employeeId, caseId };
}

/** Navigate from anywhere in the HRM app (sidebar, deep links, login). */
export function navigateHrmPage(page: string, query?: Record<string, string>): void {
  const path = buildHrmPath(page, query);
  if (navigateImpl) {
    navigateImpl(path);
    return;
  }
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Open Lifecycle → Onboarding focused on one hire. */
export function navigateHrmOnboarding(opts?: {
  employeeId?: string;
  caseId?: string;
}): void {
  const query: Record<string, string> = {};
  if (opts?.employeeId) query.employeeId = opts.employeeId;
  if (opts?.caseId) query.caseId = opts.caseId;
  navigateHrmPage('onboarding', Object.keys(query).length ? query : undefined);
}
